

import { useState, createContext, useContext, FC, ReactNode, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import TickMarkIcon from '../../assets/icons/TickMarkIcon.svg'; // Success Icon
import ErrorIcon from '../../assets/icons/CrossMarkIcon.svg'; // Error Icon
import WarningIcon from '../../assets/icons/WarningIcon'; // Warning Icon component
import CloseIcon from '../../assets/icons/CloseIcon'; // Close (cross) icon component
import InfoIcon from '../../assets/icons/InfoIcon';
import { useTheme } from '../../middleware/ThemeContext';



let globalToast: ((title: string, subtitle: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void) | null = null;

export const showToastOutsideReact = (
  title: string,
  subtitle: string,
  type: 'success' | 'error' | 'warning' | 'info',
  duration?: number
) => {
  if (globalToast) {
    globalToast(title, subtitle, type, duration);
  } else {
    console.warn("Toast system is not ready yet.");
  }
};

const ToastContext = createContext<{
  showToast: (title: string, subtitle: string, type: 'success' | 'error' | 'warning' | 'info', duration?: number) => void;
} | null>(null);

const slideDown = keyframes`
  from { transform: translateY(-100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 6%;
  left: 38%;
  transform: translate(-38%, -38%);
  background: white;
  color: #1E1E1E;
  padding: 12px 24px;
  border-radius: 8px;
  width: 490px;
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  animation: ${slideDown} 0.3s ease forwards;
  padding-bottom: 12px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ToastIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  
  img {
    width: 40px;
    height: 40px;
  }
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #1E1E1E;
  flex-grow: 1;
  font-family: 'Raleway', sans-serif;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #585858;
  font-weight: 400;
  font-family: 'Raleway', sans-serif;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 3px;
  background-color: #e0e0e0;
  border-radius: 5px;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const ProgressBar = styled.div<{ progress: number; color: string }>`
  height: 100%;
  width: ${(props) => props.progress}%;
  background-color: ${(props) => props.color};
  border-radius: 5px;
  transition: width 0.1s linear;
`;

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [title, setTitle] = useState('');
  const [subtitle, setSubTitle] = useState('');
  const [progress, setProgress] = useState(100);
  const [toastDuration, setToastDuration] = useState(10000);
  const [toastType, setToastType] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const remainingRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);
  const initialDurationRef = useRef<number>(2500);
  const { theme } = useTheme();
  const showToast = (title: string, subtitle: string, type: 'success' | 'error' | 'warning' | 'info', duration: number = 2500) => {
    // clear any existing timers

    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setTitle(title);
    setSubTitle(subtitle);
    setToastType(type);
    setProgress(100);

    // For success toasts we auto-dismiss and show progress.
    // For error/warning toasts we keep them on screen until manually closed.
    if (type === 'success') {
      const ms = duration;
      setToastDuration(ms);
      initialDurationRef.current = ms;
      // setup remaining time and clear flags
      remainingRef.current = ms;
      isPausedRef.current = false;

      // interval to update progress based on remaining time
      const intervalId = window.setInterval(() => {
        remainingRef.current = Math.max(0, remainingRef.current - 100);
        const newProgress = (remainingRef.current / ms) * 100;
        setProgress(newProgress);
        if (remainingRef.current <= 0) {
          if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 100);
      intervalRef.current = intervalId;

      // timeout to finally close the toast
      const timeoutId = window.setTimeout(() => {
        handleClose();
      }, ms);
      timeoutRef.current = timeoutId;
    } else {
      // persistent: keep visible until user presses close. Leave progress full.
      setToastDuration(0);
    }
  };

  const handleClose = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // reset tracking refs
    remainingRef.current = 0;
    isPausedRef.current = false;
    setSubTitle('');
    setTitle('');
  };
  globalToast = showToast;
  // Close toast when navigation happens. We avoid using useLocation so this
  // provider can be mounted outside a Router. Instead we patch history.pushState
  // / replaceState once to dispatch a 'locationchange' event and listen for it.
  useEffect(() => {
    // patch history API once
    const win = window as any;
    if (!win.__toast_history_patched) {
      const winHistory = window.history as any;
      const pushState = winHistory.pushState;
      const replaceState = winHistory.replaceState;
      winHistory.pushState = function (...args: any[]) {
        const ret = pushState.apply(winHistory, args as any);
        window.dispatchEvent(new Event('locationchange'));
        return ret;
      } as any;
      winHistory.replaceState = function (...args: any[]) {
        const ret = replaceState.apply(winHistory, args as any);
        window.dispatchEvent(new Event('locationchange'));
        return ret;
      } as any;
      window.addEventListener('popstate', () => window.dispatchEvent(new Event('locationchange')));
      win.__toast_history_patched = true;
    }

    const onLocationChange = () => {
      if (title) handleClose();
    };

    window.addEventListener('locationchange', onLocationChange);
    return () => {
      window.removeEventListener('locationchange', onLocationChange);
    };
    // we intentionally depend on `title` so visible toast closes on navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const handleMouseEnter = () => {
    if (toastType !== 'success') return;
    if (isPausedRef.current) return;
    // pause timers
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isPausedRef.current = true;
  };

  const handleMouseLeave = () => {
    if (toastType !== 'success') return;
    if (!isPausedRef.current) return;
    const ms = initialDurationRef.current || toastDuration || 2500;
    // resume timers with remaining time
    const intervalId = window.setInterval(() => {
      remainingRef.current = Math.max(0, remainingRef.current - 100);
      const newProgress = (remainingRef.current / ms) * 100;
      setProgress(newProgress);
      if (remainingRef.current <= 0) {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 100);
    intervalRef.current = intervalId;

    const timeoutId = window.setTimeout(() => {
      handleClose();
    }, remainingRef.current);
    timeoutRef.current = timeoutId;
    isPausedRef.current = false;
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {title && (
        <ToastContainer onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {/* Close button only for non-success (persistent) toasts */}
          {toastType !== 'success' && (
            <CloseButton onClick={handleClose} aria-label="Close toast">
              <CloseIcon size={16} color="#1E1E1E" />
            </CloseButton>
          )}

          <ToastIconWrapper >
            {toastType === 'success' && <img src={TickMarkIcon} alt="Success" />}
            {toastType === 'error' && <img src={ErrorIcon} alt="Error" />}
            {toastType === 'warning' && <WarningIcon size={28} color="#F0A500" />}
            {toastType === 'info' && <InfoIcon size={28} />}

          </ToastIconWrapper>
          <div>
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </div>

          {/* Show progress bar only for auto-dismissing toasts (success) */}
          {toastType === 'success' && (
            <ProgressBarContainer>
              <ProgressBar progress={progress} color={toastType === 'success' ? '#01E17B' : toastType === 'error' ? '#F04248' : '#F0A500'} />
            </ProgressBarContainer>
          )}
        </ToastContainer>
      )}
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context.showToast;
};



